"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { cn } from "@/lib/utils";
import styles from "./meteors.module.css";

type MeteorsProps = {
  number?: number;
  minDelay?: number;
  maxDelay?: number;
  minDuration?: number;
  maxDuration?: number;
  angle?: number;
  className?: string;
};

/**
 * Magic UI meteors — https://magicui.design/docs/components/meteors
 *
 * Note: registry sets `--angle: -angle`, which with translateX(-500px) moves
 * streaks *up* and off a full-viewport host. We keep the prop default (215)
 * but apply it as a positive CSS angle so the shower crosses the hero downward.
 */
export function Meteors({
  number = 20,
  minDelay = 0.2,
  maxDelay = 1.2,
  minDuration = 2,
  maxDuration = 10,
  angle = 215,
  className,
}: MeteorsProps) {
  const [meteorStyles, setMeteorStyles] = useState<CSSProperties[]>([]);

  useEffect(() => {
    const stylesList = Array.from({ length: number }, () => ({
      ["--angle" as string]: `${angle}deg`,
      top: "-5%",
      left: `${Math.floor(Math.random() * 100)}%`,
      animationDelay: `${Math.random() * (maxDelay - minDelay) + minDelay}s`,
      animationDuration: `${Math.floor(
        Math.random() * (maxDuration - minDuration) + minDuration,
      )}s`,
    }));
    setMeteorStyles(stylesList);
  }, [number, minDelay, maxDelay, minDuration, maxDuration, angle]);

  return (
    <>
      {meteorStyles.map((style, idx) => (
        <span
          key={idx}
          style={style}
          className={cn(styles.meteor, className)}
        >
          <span className={styles.tail} />
        </span>
      ))}
    </>
  );
}
